import React from 'react'
import { Container, Element, Form, Icon, Section, Loader, Progress } from 'react-bulma-components'
import Layout from '../../components/layout/layout'
import MissionsFilters from '../../components/missions/filters'
import useStore from '../../store'
import ComponentHeader from '../../components/sections/component-header'
import SummaryHeader from '../../components/summary/summary-header'
import Protected, { isAuthorizedFor } from '../../components/protected/protected'
import { useSummaryQuery } from '../../queries/missions'
import ExportButton from '../../components/summary/export-button'
import {
  convertDateToUTCKeepingTime,
  formatCompleteDate,
  getWeekdayMondayBased,
} from '../../utils/date'
import CreatePreBillingModal from '../../components/pre-bilings/modals/create-pre-billing-modal'
import { PreBillingIcon } from '../../components/icons'
import { useModal } from '../../components/modal'
import ButtonWithTooltip from '../../components/button-with-tooltip/button-with-tooltip'
import { chunk, sortBy, upperFirst } from 'lodash'
import SummaryPagination from '../../components/summary/summary-pagination'
import { FiltersResourcesEnum } from '../../store/filters'
import { getDay } from 'date-fns'
import SummaryTableV2 from '../../components/summary/table/summary-table-v2'

const Summary: React.FunctionComponent = () => {
  const filters = useStore(state => state.filters[FiltersResourcesEnum.missions])

  const { start, end, displayDay, summaryMinimalMode } = filters
  const pagination = useStore(state => state.filters.pagination.summary)
  const setPagination = useStore(state => state.filters.setPagination)

  const summaryQuery = useSummaryQuery()

  const summaryData = summaryQuery.data
    ? {
        ...summaryQuery.data,
        users:
          chunk(
            sortBy(summaryQuery.data.users || [], [
              item => (item.user.lastName || '').toLowerCase(),
              item => (item.user.firstName || '').toLowerCase(),
              'user._id',
              'interimAgencyOrEmployerId',
            ]),
            pagination.itemsPerPage,
          )[pagination.currentPage - 1] || [],
      }
    : null

  const missionsCount = summaryQuery.data?.users?.length || 0

  const totals = summaryQuery.data?.totals

  React.useEffect(() => {
    // TODO: maybe managed by table component?
    const summaryItemsCount = summaryQuery.data?.users?.length || 1
    if (summaryItemsCount / pagination.itemsPerPage < pagination.currentPage - 1) {
      setPagination('summary', { currentPage: 1, itemsPerPage: pagination.itemsPerPage })
    }
  }, [pagination.currentPage, summaryQuery.data])

  //Pre billing modal state
  const preBillingModal = useModal()
  const preBillingDisabled = filters.interimAgency?.length !== 1

  return (
    <Layout>
      {!preBillingDisabled && filters.interimAgency?.[0] && (
        <CreatePreBillingModal
          {...preBillingModal}
          interimAgencyId={filters.interimAgency[0]}
          start={start}
          end={end}
          service={filters.service}
          jobTitle={filters.jobTitle}
        />
      )}
      <Section pb={2} px={1} pt={5}>
        <Container breakpoint={'fluid'}>
          <ComponentHeader mb={3}>
            <ComponentHeader.Left>
              <Element backgroundColor="primary-light" style={{ borderRadius: '5px' }} p={3}>
                <MissionsFilters
                  showDayFilter
                  showSummaryMinimalModeFilter
                  showEventsFilter
                  disabledQueries={summaryQuery.isLoading}
                />
              </Element>
            </ComponentHeader.Left>
            <ComponentHeader.Right>
              <Form.Field>
                <Form.Control>
                  <Element renderAs="span" mr={2}>
                    <ExportButton
                      filters={{
                        ...filters,
                        start: filters.start && convertDateToUTCKeepingTime(filters.start),
                        end: filters.end && convertDateToUTCKeepingTime(filters.end),
                      }}
                    />
                  </Element>
                </Form.Control>
              </Form.Field>
              <Protected
                roles={['employerMember']}
                action={{ resource: 'periodSummaries', name: 'create' }}
              >
                <ButtonWithTooltip
                  fullwidth
                  onClick={() => preBillingModal.setIsDisplayed(true)}
                  color={'primary'}
                  disabled={preBillingDisabled}
                  tooltip={{
                    text: "Séléctionner une seule agence d'intérim pour créer une pré-facturation",
                    show: preBillingDisabled,
                    id: 'pre-billing-action',
                  }}
                >
                  <Icon>
                    <PreBillingIcon />
                  </Icon>
                  <span>Pré-facturation</span>
                </ButtonWithTooltip>
              </Protected>
            </ComponentHeader.Right>
          </ComponentHeader>
        </Container>
      </Section>
      <Container breakpoint={'fluid'}>
        <Section p={4} backgroundColor="white-bis">
          <SummaryHeader
            startOfWeek={start as Date}
            endOfWeek={end as Date}
            interimAgencyId={
              filters.interimAgency?.length === 1 ? filters.interimAgency[0] : undefined
            }
            currentDay={
              displayDay &&
              totals && {
                label: `${upperFirst(formatCompleteDate(displayDay))}`,
                ...totals.days[getWeekdayMondayBased(getDay(new Date(displayDay)))],
              }
            }
            totals={totals}
            missionsCount={missionsCount}
            showEmployerCosts={Boolean(
              isAuthorizedFor(['employerMember']) ||
                (isAuthorizedFor(['interimAgencyMember']) && filters.employer?.length === 1),
            )}
            employerRules={summaryQuery.data?.employerRules}
          />
        </Section>
        <Section className="summary-table" style={{ padding: 0 }}>
          {!summaryQuery.isLoading && (
            <SummaryPagination
              currentPage={pagination.currentPage}
              missionsPerPage={pagination.itemsPerPage}
              missionsCount={missionsCount}
              onChange={currentPage =>
                setPagination('summary', { currentPage, itemsPerPage: pagination.itemsPerPage })
              }
            />
          )}
          <Container breakpoint={'fluid'} style={{ padding: 0 }} mt={5}>
            <Element textSize={7}>
              {!summaryQuery.isLoading && summaryData ? (
                <SummaryTableV2
                  data={summaryData}
                  start={start}
                  end={end}
                  minimalMode={summaryMinimalMode}
                  displayDay={displayDay}
                />
              ) : (
                <Element display="flex" justifyContent="center" flexDirection="column">
                  <Element textSize={5} display="flex" justifyContent="center" my={5}>
                    <Icon.Text>
                      <Loader mr={5} />
                      Mise à jour des données du récapitulatif
                    </Icon.Text>
                  </Element>
                  <Container breakpoint={'widescreen'}>
                    <Progress color="primary" size={'small'} px={6} />
                  </Container>
                </Element>
              )}
            </Element>
          </Container>
          {!summaryQuery.isLoading && (
            <SummaryPagination
              currentPage={pagination.currentPage}
              missionsPerPage={pagination.itemsPerPage}
              missionsCount={missionsCount}
              onChange={currentPage =>
                setPagination('summary', { currentPage, itemsPerPage: pagination.itemsPerPage })
              }
            />
          )}
        </Section>
      </Container>
    </Layout>
  )
}

export default Summary
